import { Table } from '@acceso/design-system';
import React, { useCallback } from 'react';

import { VideoPresencesSubTable } from '@/components/VideoPresences/components/VideoPresencesSubTable/VideoPresencesSubTable';
import { useVideoPresencesDatatable } from '@/components/VideoPresences/hooks/use-video-presences-datatable';
import { VideoPresencesManagementBar } from '@/components/VideoPresences/VideoPresencesManagementBar';
import { LogoPresenceDropdown } from '@/components/VideoPresencesInlineControls';
import { useBroadcastScheduleContext } from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/components/BroadcastScheduleProgramBodySegmentToolkitLogotipos/context/BroadcastScheduleProvider';
import { buildNewPresenceDataToSave } from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/components/BroadcastScheduleProgramBodySegmentToolkitLogotipos/helpers/batchSelectionHelpers';
import {
  FrameActionType,
  TBroadcastScheduleOnUpdateInline,
  VISUALIZATION_OPTIONS_VALUES,
  VisualizationOption,
} from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/components/BroadcastScheduleProgramBodySegmentToolkitLogotipos/types';
import { useBroadcastScheduleProgram } from '@/features/broadcast-schedule-program/context/BroadcastScheduleProgramContext';
import { TClipLogo, TPostClipLogo } from '@/features/clips/types';
import { useGroupedPresences } from '@/hooks/use-grouped-presences';
import { useSegmentPresences } from '@/hooks/use-segment-presences';
import { PresenceTypeEnum } from '@/types/presences';

type ClipToolkitLogotiposTableProps = {
  items: TClipLogo[];
  onPreview?: (item: TClipLogo, frame?: FrameActionType) => void;
  onUpdate?: (item: TClipLogo) => void;
  onUpdateInline?: TBroadcastScheduleOnUpdateInline<TPostClipLogo>;
  onDelete?: (item: TClipLogo, silently?: boolean) => void;
};

export function ClipToolkitLogotiposTable({
  items,
  onPreview,
  onUpdate,
  onUpdateInline,
  onDelete,
}: ClipToolkitLogotiposTableProps) {
  const { buildGroupOptions } = useSegmentPresences();
  const { getRowClassStyles, logosById } = useGroupedPresences<TClipLogo>(items, onPreview);
  const { groupVisualization, groupedPresences } = useBroadcastScheduleContext();
  const { logoData } = useBroadcastScheduleProgram();

  const renderEntityCell = useCallback(
    (item: TClipLogo, showSubArrows: (f: (v: boolean) => boolean) => void) => (
      <LogoPresenceDropdown
        item={item}
        logosById={logosById}
        onPreview={onPreview}
        onUpdateInline={onUpdateInline}
        parentCallback={() => showSubArrows(prev => !prev)}
      />
    ),
    [onPreview, onUpdateInline, logosById]
  );

  const { getOptions, getColumns, getLogosThumbsColumns } = useVideoPresencesDatatable({
    items,
    onPreview,
    onUpdateInline,
    onUpdate,
    onDelete,
    renderEntityCell,
  });

  const columnsByVisualization: Record<VisualizationOption, typeof getColumns> = {
    [VISUALIZATION_OPTIONS_VALUES.List]: getColumns,
    [VISUALIZATION_OPTIONS_VALUES.Thumbnails]: getLogosThumbsColumns,
  };

  const renderSubTable = useCallback(
    (item: TClipLogo) => (
      <VideoPresencesSubTable
        item={item as TClipLogo & { children: TClipLogo[] }}
        columns={getColumns}
        onPreview={onPreview}
        onUpdate={onUpdate}
        onUpdateInline={onUpdateInline}
        onDelete={onDelete}
        showSupportColumn={true}
        renderEntityCell={child => (
          <LogoPresenceDropdown
            item={child}
            logosById={logosById}
            onPreview={onPreview}
            onUpdateInline={onUpdateInline}
            hasParent
          />
        )}
        entityIdKey="logo_id"
        buildDataToSave={buildNewPresenceDataToSave as any}
        activeEntityId={logoData?.id}
        tableDataId="logos-subtable"
      />
    ),
    [getColumns, onPreview, onUpdate, onUpdateInline, onDelete, logoData?.id, logosById]
  );

  const additionalRowByVisualization: Partial<
    Record<VisualizationOption, (item: TClipLogo) => React.ReactNode>
  > = {
    [VISUALIZATION_OPTIONS_VALUES.List]: renderSubTable,
  };

  const hasCustomExtraBar = onDelete && onUpdateInline && onPreview;

  return (
    <Table<any>
      containerClassName="!overflow-x-hidden"
      className="broadcast-schedule-program-body-segment-toolkit-logos-table !w-auto min-w-full table-fixed"
      options={getOptions}
      columns={columnsByVisualization[groupVisualization] ?? getColumns}
      data={groupedPresences}
      renderAdditionalRow={additionalRowByVisualization[groupVisualization]}
      customExtraBar={
        hasCustomExtraBar && (
          <VideoPresencesManagementBar<TPostClipLogo>
            {...{ onDelete, onUpdateInline, items, onPreview }}
            groupOptions={buildGroupOptions(PresenceTypeEnum.LOGOS)}
            presenceType={PresenceTypeEnum.LOGOS}
          />
        )
      }
      ignoreWrap
      getSpecialClassNames={getRowClassStyles}
    />
  );
}
